import { useCallback, useRef, useState } from 'react';
import { CraftBlock, Meeting } from '../../types';
import type { RecordingPrefs, TranscribeLanguage } from '../../global';
import { t } from '../../lib/i18n';
import { IS_WEB } from '../../lib/platform';
import { formatTimestamp, useRecorder } from './useRecorder';
import { MIN_SUMMARY_SECONDS } from './summaryRules';
import { buildRecordingDraft, clockLabel } from './buildRecordingDraft';
import { playRecordingCue } from './recordingSounds';
import { usePreferences } from '../preferences/usePreferences';

/** Takvimden başlatılan kayıtlarda başlık/katılımcılar önden doldurulur. */
export interface RecordPrefill {
  title?: string;
  participants?: Meeting['participants'];
  /** Takvim etkinliğinin bitiş zamanı (ISO) — otomatik durdurma kullanıyor. */
  endsAt?: string;
  /** Yalnızca web: toplantı sekmesinin sesi de alınsın (bkz. RecordSourceDialog). */
  captureSystemAudio?: boolean;
  /** Mikrofon alınmasın, yalnızca sistem sesi (web'de paylaşılan sekme) — takvim kartındaki mikrofon düğmesi. */
  systemOnly?: boolean;
}

/**
 * `idle` dışındaki her durumda kayıt sayfası canlı sayılır.
 * `stopping` ses dosyasını diske yazarken, `summarizing` Claude özeti
 * beklenirken görünür — ikisi de saniyeler sürebildiği için ayrı.
 */
export type RecordingPhase = 'idle' | 'starting' | 'live' | 'stopping' | 'summarizing';

/**
 * Mobil: canlı akıştan en az bu kadar ses atıldıysa (arayüz arka planda kısıldı) döküm
 * telefonun tam dosyasından yeniden yapılır. Birkaç saniyelik takılma için değmez.
 */
const COMPLETE_FROM_FILE_MIN_GAP_SECONDS = 15;

/** Ayarlar okunamazsa (ör. dosya yoksa) kayıt yine de başlayabilmeli. */
const FALLBACK_PREFS: RecordingPrefs = { transcribeLanguage: 'tr' };

/**
 * Notlar sekmesinde yazılanları Craft bloklarına çevirir. "- " ile başlayan
 * satırlar madde, kalanlar paragraf olur — kullanıcı kayıt sırasında yazdığı
 * notu, kayıt bitince not sayfasında aynen bulsun.
 */
function notesToBlocks(notes: string, seed: number): CraftBlock[] {
  return notes
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, index) => {
      const isBullet = line.startsWith('- ') || line.startsWith('* ');
      return {
        id: `cb-${seed}-n${index}`,
        type: isBullet ? ('bullet' as const) : ('paragraph' as const),
        content: isBullet ? line.slice(2).trim() : line,
      };
    });
}

/**
 * Kayıt oturumu — kayıt ekranının tüm durumu tek yerde.
 *
 * Bilerek App seviyesinde çağrılıyor: kayıt artık bir modal değil bir sayfa,
 * yani kullanıcı kayıt sürerken Home'a ya da başka bir toplantıya geçebiliyor.
 * Kanca sayfanın içinde olsaydı, sayfadan çıkmak mikrofonu kapatırdı.
 */
export function useRecordingSession() {
  const recorder = useRecorder();
  // Kayıt sesleri ayarı (Ayarlar > Genel > Davranış).
  const { prefs } = usePreferences();
  const soundsEnabled = prefs.behavior.recordingSounds;
  const cue = useCallback(
    (kind: Parameters<typeof playRecordingCue>[0]) => {
      if (soundsEnabled) playRecordingCue(kind);
    },
    [soundsEnabled]
  );

  const [phase, setPhase] = useState<RecordingPhase>('idle');
  const [title, setTitle] = useState(() => t('Yeni Kayıt', 'New recording'));
  const [space, setSpace] = useState<Meeting['space']>('private');
  const [language, setLanguageState] = useState<TranscribeLanguage>('tr');
  const [notes, setNotes] = useState('');
  /** Kaydı bitirirken oluşan hata — `recorder.error` donanım hatalarını taşıyor. */
  const [saveError, setSaveError] = useState<string | null>(null);
  const participantsRef = useRef<Meeting['participants']>([]);
  /** Web: son seçilen kaynak — "Tekrar dene" aynısıyla başlasın. */
  const captureSystemAudioRef = useRef(false);
  const systemOnlyRef = useRef(false);
  /** Takvimden başlatıldıysa planlanan bitiş (epoch ms), yoksa null. */
  const [scheduledEndAt, setScheduledEndAt] = useState<number | null>(null);

  const isActive = phase !== 'idle';

  const begin = useCallback(
    async (prefill?: RecordPrefill) => {
      if (isActive) return; // zaten kayıtta — çağıran tarafın sayfaya dönmesi yeterli

      setTitle(prefill?.title?.trim() || t('Yeni Kayıt', 'New recording'));
      participantsRef.current = prefill?.participants ?? [];
      const endsAt = prefill?.endsAt ? Date.parse(prefill.endsAt) : NaN;
      setScheduledEndAt(Number.isFinite(endsAt) ? endsAt : null);
      setNotes('');
      setSaveError(null);
      setPhase('starting');
      captureSystemAudioRef.current = prefill?.captureSystemAudio === true;
      systemOnlyRef.current = prefill?.systemOnly === true;

      // Kayıt tercihleri (transkript dili) — kişisel tercihlerle karışmasın diye ayrı ad.
      // Okunamazsa varsayılanlarla devam — kayıt engellenmemeli. Beklenmeden
      // kaydediciye veriliyor: web'de paylaşım penceresi tıklamanın içinde açılmalı.
      const language = window.zabit
        .getRecordingPrefs()
        .catch(() => FALLBACK_PREFS)
        .then((prefs) => {
          setLanguageState(prefs.transcribeLanguage);
          return prefs.transcribeLanguage;
        });

      try {
        await recorder.start({ language, captureSystemAudio: captureSystemAudioRef.current, systemOnly: systemOnlyRef.current });
        setPhase('live');
        cue('start');
      } catch {
        // Hata mesajı recorder.error üzerinden ekranda; sayfa açık kalıp
        // "Tekrar dene" sunuyor.
        setPhase('idle');
        cue('error');
      }
    },
    [cue, isActive, recorder]
  );

  const retry = useCallback(async () => {
    setSaveError(null);
    setPhase('starting');
    try {
      await recorder.start({ language, captureSystemAudio: captureSystemAudioRef.current, systemOnly: systemOnlyRef.current });
      setPhase('live');
      cue('start');
    } catch {
      setPhase('idle');
      cue('error');
    }
  }, [cue, language, recorder]);

  const changeLanguage = useCallback(
    (next: TranscribeLanguage) => {
      setLanguageState(next);
      // Deepgram bağlantıları yeni dille yeniden açılır; kayıt kesilmez.
      recorder.setLanguage(next);
    },
    [recorder]
  );

  /**
   * Kaydı bitirir, sesi diske yazar, transkript varsa AI özeti ister ve
   * kaydedilmeye hazır bir toplantı taslağı döndürür. Kalıcılık çağıranın
   * işi (bkz. features/meetings) — bu kanca yalnızca kaydı biliyor.
   */
  const stopAndSave = useCallback(async (): Promise<Omit<Meeting, 'id'> | null> => {
    setPhase('stopping');
    cue('stop');
    const hadSystemAudio = recorder.hasSystemAudio;

    // Ses dosyasını diske yazmak başarısız olabilir (disk dolu, klasör
    // salt-okunur). Eskiden bu hata yukarı kaçıyor ve `phase` 'stopping'te
    // takılı kalıyordu: "Bitir & Özetle" düğmesi sonsuza kadar
    // "Kaydediliyor…" gösteriyor, kayıt da bitmiyordu.
    let result: Awaited<ReturnType<typeof recorder.stop>>;
    try {
      result = await recorder.stop();
    } catch (err) {
      setPhase('idle');
      setSaveError((err as Error).message || t('Kayıt diske yazılamadı.', 'Could not save the recording to disk.'));
      cue('error');
      return null;
    }

    if (!result) {
      setPhase('idle');
      return null;
    }
    setSaveError(null);

    // Mobil: arayüz arka planda kısılınca canlı akıştan ses atılmış olabilir — o kısım canlı
    // dökümde yok. Eşiği geçtiyse telefonun kesintisiz dosyası sunucuda yeniden yazıya dökülür;
    // kota yalnızca canlı oturumun saymadığı kadar düşer (web/lib/usage/live-credit.ts).
    let transcript = result.transcript;
    if ((window.zabit.nativeMic?.lastGapSeconds() ?? 0) >= COMPLETE_FROM_FILE_MIN_GAP_SECONDS) {
      try {
        const full = await window.zabit.transcribeAudioFile(result.filePath, {
          liveWindow: { fromISO: result.startedAtISO, toISO: result.endedAtISO },
        });
        if (full.transcript.length > 0) transcript = full.transcript;
      } catch (err) {
        // Canlı döküm (eksik de olsa) kalır.
        console.warn('[kayıt] eksik döküm dosyadan tamamlanamadı:', (err as Error).message);
      }
    }

    const now = new Date();
    const draft = await buildRecordingDraft({
      transcript,
      durationSeconds: result.durationSeconds,
      filePath: result.filePath,
      // Kullanıcı başlık yazdıysa o kalır; yazmadıysa başlığı yapay zekâ özeti verir.
      userTitle: title,
      fallbackTitle:
        result.durationSeconds < MIN_SUMMARY_SECONDS
          ? t(`Kısa kayıt · ${clockLabel(now)}`, `Short recording · ${clockLabel(now)}`)
          : t('Yeni Kayıt', 'New recording'),
      recordedAt: now,
      space,
      participants: participantsRef.current,
      tags: [t('Kayıt', 'Recording')],
      callout: ({ speakers, hasTranscript }) => {
        const speakerText = hasTranscript ? ` ${t('Konuşmacılar', 'Speakers')}: ${speakers.join(', ')}.` : '';
        const length = formatTimestamp(result.durationSeconds);
        // Web'de sistem sesi yok: karşı taraf yalnızca paylaşılan sekmeden gelir.
        if (IS_WEB && !hadSystemAudio) {
          return t(`🎙️ ${length} sürdü, mikrofondan kaydedildi.${speakerText}`, `🎙️ Lasted ${length}; recorded from the microphone.${speakerText}`);
        }
        return IS_WEB
          ? t(
              `🎙️ ${length} sürdü, mikrofon + toplantı sekmesi birlikte kaydedildi.${speakerText}`,
              `🎙️ Lasted ${length}; microphone and the meeting tab were recorded together.${speakerText}`
            )
          : t(
              `🎙️ ${length} sürdü, mikrofon + sistem sesi karıştırılarak kaydedildi.${speakerText}`,
              `🎙️ Lasted ${length}; microphone and system audio were recorded together.${speakerText}`
            );
      },
      extraBlocks: notesToBlocks(notes, Date.now()),
      onSummarizing: () => setPhase('summarizing'),
    });

    setPhase('idle');
    return draft;
  }, [cue, notes, recorder, space, title]);

  /** İptal — kaydı diske YAZMADAN durdur. */
  const cancel = useCallback(async () => {
    await recorder.discard();
    setPhase('idle');
    setNotes('');
    setSaveError(null);
  }, [recorder]);

  return {
    phase,
    isActive,
    isRecording: recorder.isRecording,
    isPaused: recorder.isPaused,
    seconds: recorder.seconds,
    micLevel: recorder.micLevel,
    systemLevel: recorder.systemLevel,
    hasSystemAudio: recorder.hasSystemAudio,
    error: saveError ?? recorder.error,
    notice: recorder.notice,
    liveTranscript: recorder.liveTranscript,
    pendingLines: recorder.pendingLines,
    togglePause: recorder.togglePause,
    getSilence: recorder.getSilence,
    scheduledEndAt,
    title,
    setTitle,
    space,
    setSpace,
    language,
    setLanguage: changeLanguage,
    notes,
    setNotes,
    begin,
    retry,
    stopAndSave,
    cancel,
  };
}

export type RecordingSession = ReturnType<typeof useRecordingSession>;
